import React from 'react';
import {
  ComboBox,
  ComboBoxFilterChangeEvent
} from '@progress/kendo-react-dropdowns';
import { Label } from '@progress/kendo-react-labels';
import { Button } from '@progress/kendo-react-buttons';
import { Loader } from '@progress/kendo-react-indicators';
import { Input, TextArea } from '@progress/kendo-react-inputs';
import { filterBy, FilterDescriptor } from '@progress/kendo-data-query';
import { Window, WindowActionsBar } from '@progress/kendo-react-dialogs';

import {
  useGetGameLatestDocument,
  useGetGames
} from '../hooks/services/use-games';
import { appStore } from '../stores/app-store';
import { useToast } from '../contexts/toast-context';
import { GameResponse } from '../types/responses/game-response';
import { ZoneLibrary } from '../types/responses/zone-library-response';
import { useZoneLibraryById } from '../hooks/services/use-zones-library';
import { ImportZones, UpdateGameDocumentAsync } from '../utils/game-document';

export const ZoneCopyToGameWindow: React.FC<{
  zoneId: string;
  onClose: Function;
}> = ({ zoneId, onClose }) => {
  const { onToastOpen } = useToast();
  const { data: zoneById, isLoading: isLoadingZoneById } = useZoneLibraryById(
    zoneId,
    false,
    {
      onSuccess: (data: ZoneLibrary) => {
        setZoneForm({
          name: data.name,
          description: data.description
        });
      }
    }
  );
  const { data: games, isLoading: isLoadingGame } = useGetGames();
  const { trigger } = useGetGameLatestDocument();

  const [zoneForm, setZoneForm] = React.useState<{
    name: string;
    description: string;
  }>({
    name: '',
    description: ''
  });
  const [selectedGame, setSelectedGame] = React.useState({
    id: '',
    name: ''
  });
  const [gameData, setGameData] = React.useState<GameResponse[]>(
    games?.data ?? []
  );

  const filterGameData = React.useCallback(
    (filter: FilterDescriptor) => {
      return filterBy(games?.data ?? [], filter);
    },
    [games]
  );

  const timeout = React.useRef<any>(null);
  const filterGameChange = (event: ComboBoxFilterChangeEvent) => {
    clearTimeout(timeout.current);
    timeout.current = setTimeout(() => {
      setGameData(filterGameData(event.filter));
    }, 500);
  };

  const onCopyZone = async () => {
    if (!zoneById) return;
    appStore.showLoading();
    try {
      const gameDocument = await trigger({ id: selectedGame.id });

      const { content } = zoneById;
      content.zones[0].name = zoneForm.name;
      content.zones[0].description = zoneForm.description;
      content.maps[0].name = zoneForm.name;
      content.maps[0].description = zoneForm.description;

      const updatedGameDocument = await ImportZones(gameDocument, content);
      await UpdateGameDocumentAsync(
        parseInt(selectedGame.id),
        updatedGameDocument
      );

      onToastOpen({
        title: 'Success',
        body: `Zone map copied to ${selectedGame.name}.`,
        style: 'success'
      });
    } catch (error) {
      console.error('Failed to copy zone:', error);
      onToastOpen({
        title: 'Error',
        body: 'Failed to copy zone map.',
        style: 'error'
      });
    } finally {
      appStore.hideLoading();
      onClose();
    }
  };

  React.useEffect(() => {
    if (!games) return;
    setGameData(games.data ?? []);
  }, [games]);

  return (
    <Window
      title={'Copy Zone Map to Game'}
      initialWidth={500}
      initialHeight={450}
      onClose={() => onClose()}>
      {isLoadingZoneById ? (
        <div
          className={
            'd-flex flex-column w-100 h-100 align-items-center justify-content-center'
          }>
          <Loader type={'infinite-spinner'} size={'large'} />
          <span>Loading zone map content</span>
        </div>
      ) : (
        <>
          <div className="mb-3">
            <label className="k-label">Name</label>
            <Input
              value={zoneForm.name}
              onChange={(e) =>
                setZoneForm((prev) => ({ ...prev, name: e.value }))
              }
              style={{ width: '100%' }}
            />
          </div>
          <div className="mb-3">
            <label className="k-label">Description</label>
            <TextArea
              value={zoneForm.description}
              onChange={(e) =>
                setZoneForm((prev) => ({ ...prev, description: e.value }))
              }
              style={{ width: '100%' }}
            />
          </div>

          <Label>Game name</Label>
          <ComboBox
            loading={isLoadingGame}
            disabled={isLoadingGame}
            data={gameData}
            textField={'name'}
            dataItemKey={'id'}
            filterable={true}
            onFilterChange={filterGameChange}
            onChange={(e) =>
              setSelectedGame({
                id: e.value ? e.value.id : null,
                name: e.value ? e.value.name : null
              })
            }
            allowCustom={false}
          />
        </>
      )}

      <WindowActionsBar>
        <Button themeColor={'secondary'} onClick={() => onClose()}>
          Cancel
        </Button>
        <Button themeColor={'primary'} onClick={onCopyZone}>
          Copy
        </Button>
      </WindowActionsBar>
    </Window>
  );
};
